/**
 * Sieve — persistence (§9).
 *
 * Local device only for v1: no accounts, no login, nothing leaves the device
 * (§13.4). IndexedDB is the primary store with a localStorage fallback. The
 * document is versioned with a forward migration path.
 */

import { SCHEMA_VERSION } from './constants';
import {
  defaultState,
  emptyModeState,
  type ModeState,
  type PersistedState,
  type Stats,
} from './types';

const DB_NAME = 'sieve';
const STORE = 'state';
const KEY = 'root';
const LS_KEY = 'sieve:state';

// ─── Migration ───────────────────────────────────────────────────────────────

type AnyState = Record<string, unknown> & { schemaVersion?: number };

/** Fills a ModeState from a partial/legacy object, defaulting missing fields. */
function mergeModeState(raw: unknown): ModeState {
  const base = emptyModeState();
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Partial<ModeState>;
  const stats: Stats = { ...base.stats, ...(r.stats as object) };
  const h = stats.mistakeHistogram ?? [0, 0, 0, 0];
  stats.mistakeHistogram = [h[0] ?? 0, h[1] ?? 0, h[2] ?? 0, h[3] ?? 0];
  return {
    lastPuzzleId: r.lastPuzzleId ?? 0,
    lastCompletedDailyId: r.lastCompletedDailyId ?? 0,
    stats,
    inProgress: r.inProgress ?? null,
    dailyResults: r.dailyResults ?? {},
    archiveResults: r.archiveResults ?? {},
  };
}

/**
 * Migrates a loaded document up to the current schema version (2). Additive and
 * idempotent; a partial document never crashes the app.
 *
 * v1 (flat, prime-only) → v2: the flat prime fields move under `prime`, an empty
 * `multiples` sibling is added, and existing prime players keep prime as their
 * default landing (new users default to the easier Multiples daily).
 */
export function migrate(raw: AnyState | null): PersistedState {
  const base = defaultState();
  if (!raw || typeof raw !== 'object') return base;

  const settings = { ...base.settings, ...(raw.settings as object) };
  const rapidBest = typeof raw.rapidBest === 'number' ? raw.rapidBest : 0;

  // Already v2 (namespaced) — merge each block onto defaults.
  if (raw.prime || raw.multiples || raw.schemaVersion === 2) {
    return {
      schemaVersion: SCHEMA_VERSION,
      lastMode: raw.lastMode === 'prime' ? 'prime' : 'multiples',
      prime: mergeModeState(raw.prime),
      multiples: mergeModeState(raw.multiples),
      rapidBest,
      settings,
    };
  }

  // v1 flat prime document → lift into the `prime` namespace.
  const prime = mergeModeState(raw);
  return {
    schemaVersion: SCHEMA_VERSION,
    // A returning prime player keeps prime as their default; brand-new → Multiples.
    lastMode: prime.stats.played > 0 ? 'prime' : 'multiples',
    prime,
    multiples: emptyModeState(),
    rapidBest,
    settings,
  };
}

// ─── IndexedDB ───────────────────────────────────────────────────────────────

function idbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined';
  } catch {
    return false;
  }
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(): Promise<AnyState | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = () => resolve((req.result as AnyState) ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(state: PersistedState): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(state, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── localStorage fallback ───────────────────────────────────────────────────

function lsGet(): AnyState | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as AnyState) : null;
  } catch {
    return null;
  }
}

function lsSet(state: PersistedState): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    /* private mode / quota — nothing else we can do locally */
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/** Loads and migrates the persisted state, choosing the best available store. */
export async function loadState(): Promise<PersistedState> {
  if (idbAvailable()) {
    try {
      const raw = await idbGet();
      if (raw) return migrate(raw);
      // First run under IDB — but an older localStorage doc may exist; adopt it.
      const legacy = lsGet();
      if (legacy) {
        const migrated = migrate(legacy);
        await idbSet(migrated).catch(() => {});
        return migrated;
      }
      return defaultState();
    } catch {
      // IDB blew up (e.g. Safari private mode) — fall back to localStorage.
    }
  }
  return migrate(lsGet());
}

/** Persists state to IndexedDB with a localStorage mirror for resilience. */
export async function saveState(state: PersistedState): Promise<void> {
  state.schemaVersion = SCHEMA_VERSION;
  lsSet(state); // cheap, synchronous mirror
  if (idbAvailable()) {
    try {
      await idbSet(state);
    } catch {
      /* mirror already written */
    }
  }
}
